/**
 * Review question architecture — ported EXACTLY from the Pennysaver bot
 * (docs/reference/pennysaver_main.py) and docs/pennysaver-review-architecture-S73.md.
 * Shared by the client UI (render the controls) and the server (validate + compose).
 *
 * Do NOT invent options. These lists are the locked spec.
 * @see docs/pennysaver-review-architecture-S73.md
 */

/** The 10 review voices. Voice comes through as attitude, NOT forced dialect. */
export const VOICES: Record<string, string> = {
  Yinzer:
    'a proud born-and-raised Pittsburgher; warm, casual, loyal. Pittsburgh shows in attitude and what they notice, not in dialect words.',
  Transplant: 'someone who moved here and fell for the city; appreciative, discovering tone',
  Foodie: 'an enthusiastic food lover; vivid about flavor and technique, never snobby',
  'Blue Collar': 'down-to-earth, no-frills, value-and-portions, straight talk',
  Family: 'a parent reviewing for families; kid-friendliness, space, ease, value',
  'Sports Fan': 'game-day energy; great before/after the game, group-friendly, hearty',
  'Date Night': 'romantic, ambiance-forward, cozy and memorable',
  'Late Night': "the late-night crowd voice; what's good after hours, vibe and convenience",
  'College Kid': 'budget-savvy student; cheap eats, big portions, fun',
  'Neighborhood Regular': 'a loyal local who knows the spot; familiar, trusted, repeat-visit warmth',
};

export const VOICE_KEYS = Object.keys(VOICES);
export const DEFAULT_VOICE = 'Yinzer';

/** Three Rivers rating. Value "0" = "Wasn't for me" (no rivers). */
export type RatingValue = '0' | '1' | '2' | '3';

export const RIVERS: Record<RatingValue, { label: string; blurb: string }> = {
  '3': { label: '3 Rivers', blurb: 'must-go, Pittsburgh legendary' },
  '2': { label: '2 Rivers', blurb: 'great' },
  '1': { label: '1 River', blurb: 'good' },
  '0': { label: "Wasn't for me", blurb: 'no rivers' },
};

/** Rating options in display order (best first). */
export const RATING_ORDER: RatingValue[] = ['3', '2', '1', '0'];

/** Typed "basics" fields. `required` matches kickoff §4 (ordered + dish are required). */
export interface BasicField {
  key: 'ordered' | 'dish_to_get' | 'go_if' | 'tip' | 'anything';
  label: string;
  hint: string;
  required: boolean;
}

export const BASICS: BasicField[] = [
  { key: 'ordered', label: 'The dish/es', hint: 'list what you and your group got', required: true },
  { key: 'dish_to_get', label: 'The dish to get', hint: 'what should the next person order?', required: true },
  { key: 'go_if', label: 'Go here if…', hint: 'finish the sentence', required: false },
  { key: 'tip', label: 'A tip for next time', hint: 'where to sit, when to go, order ahead…', required: false },
  { key: 'anything', label: 'Anything else? (optional)', hint: 'about the spot only', required: false },
];

// Two independent scales (item 9): PRICING is factual; VALUE_SCALE is a positive worth-it scale.
export const PRICING = ['Cheap eats', 'Moderate', 'Pricey', 'Big splurge'] as const;
export const VALUE_SCALE = ['Fair value', 'Good value', 'Great value', 'Worth every penny'] as const;

// Service stays all-positive (kindly-spun lowest → top): better adjectives, more rungs.
export const SERVICE = ['Just okay', 'Friendly', 'Attentive', 'Great', 'Outstanding'] as const;

export const VIBES = [
  'Cozy', 'Lively', 'Casual', 'Romantic', 'Family-friendly', 'Trendy',
  'Laid-back', 'Quiet', 'Bustling', 'Outdoor seating', 'Quick bite', 'Late-night',
  'Date-worthy', 'Dog-friendly', 'Sports on', 'Live music', 'Great patio', 'Instagram-worthy',
] as const;
export const VIBE_MAX = 5;

export const GOOD_FOR = [
  'Date night', 'Family', 'Groups', 'Solo', 'Business', 'Late-night', 'Catching up',
  // no-seating / pickup styles (item 5)
  'Takeout only', 'Counter service', 'Grab and go', 'Quick pickup', 'Delivery',
] as const;
export const GOOD_FOR_MAX = 5;

export const PARTY = ['Solo', 'Two', '3-4', '5 or more'] as const;
export const WHEN = ['Breakfast', 'Brunch', 'Lunch', 'Happy hour', 'Afternoon', 'Dinner', 'Late night'] as const;
// Positive / neutral only — no "first and last time" (positivity model). Up to 5.
export const BACK = ['Definitely', 'Probably', 'Maybe', 'If I’m nearby', 'Worth another look'] as const;

/** "Wanna leave more?" deep categories — multi-select chips + "add your own". */
export interface DeepCategory {
  key: 'food' | 'atmosphere' | 'service_ops' | 'value' | 'access' | 'found';
  title: string;
  options: string[];
}

export const DEEP: DeepCategory[] = [
  {
    key: 'food',
    title: '🍽️ Food & drink',
    options: [
      'Standout dish', 'Big portions', 'Fresh ingredients', 'Great drinks', 'Killer dessert',
      'Veg/vegan options', 'GF friendly', 'Spice done right', 'Good for sharing',
    ],
  },
  {
    key: 'atmosphere',
    title: '🏠 Atmosphere',
    options: ['Cozy', 'Lively', 'Quiet to talk', 'Great patio', 'Good music', 'Clean and tidy', 'Comfy seating', 'Romantic'],
  },
  {
    key: 'service_ops',
    title: '🛎️ Service & ops',
    options: ['Fast', 'Super friendly', 'Attentive', 'No real wait', 'Easy reservations', 'Great takeout', 'Order spot-on', 'Accommodating'],
  },
  {
    key: 'value',
    title: '💵 Value & deals',
    options: ['Worth every penny', 'Big portions for the price', 'Great happy hour', 'BYOB', 'Good lunch special'],
  },
  {
    key: 'access',
    title: '♿ Access & logistics',
    options: ['Easy parking', 'Wheelchair accessible', 'Kid-friendly', 'Dog-friendly patio', 'Near transit', 'Clean restrooms'],
  },
  {
    key: 'found',
    title: '📣 How’d you find it?',
    options: ['Brockett', 'A friend', 'Walked by', 'Social media', 'Reddit', 'PGH Bites feed'],
  },
];

/** Human labels for every captured field (used in the compose prompt + summaries). */
export const FIELD_LABELS: Record<string, string> = {
  ordered: 'What they ordered',
  dish_to_get: 'Dish to get',
  go_if: 'Go here if',
  tip: 'Tip',
  anything: 'Anything else',
  pricing: 'Pricing',
  worth: 'Value',
  service: 'Service',
  vibe: 'Vibe',
  good_for: 'Good for',
  party: 'Party size',
  when: 'Visit time',
  back: 'Would return',
  food: 'Food & drink',
  atmosphere: 'Atmosphere',
  service_ops: 'Service & ops',
  value: 'Value & deals',
  access: 'Access & logistics',
  found: 'Found via',
};

/** Order used when summarizing fields for the AI writer (matches main.py field_summary order). */
export const SUMMARY_ORDER = [
  'ordered', 'dish_to_get', 'go_if', 'tip', 'anything', 'pricing', 'worth', 'service',
  'good_for', 'party', 'when', 'back', 'vibe',
  'food', 'atmosphere', 'service_ops', 'value', 'access', 'found',
] as const;

/** Render the rivers as a friendly string (no emoji dependency in copy). */
export function threeRivers(value: RatingValue): string {
  if (value === '0') return "Wasn't for me";
  return RIVERS[value].label;
}
